import React, { useCallback, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import qs from 'qs';
import { isEmpty } from 'lodash-es';

import QuarterlyPerformanceChart from './QuarterlyPerformanceChart';
import ForecastsTableScrollableContainer from './ForecastsTableScrollableContainer';
import skeletons from '../Skeletons';
import ForecastsChartLayout from '../../../../app-insights/_shared/components/Forecasts/ForecastsChartLayout';
import useTableAutoScroll from '../../../../../components/StickyTableLayout/hooks/useTableAutoScroll';
import ConsensusSourceText from '../../../../../components/ConsensusSourceText';
import { makeTableList, tableNames } from './config';

const emptyObject = {};

const QuarterlyPerformance = ({
  series,
  activeForecast = emptyObject,
  pending,
  metadata = emptyObject,
  allowForecastsWhitoutConsensus,
}) => {
  const [view, setView] = useState('actuals');
  const [scrollableContainerRef, onChartZoomChange] = useTableAutoScroll();

  const tableForecast = useMemo(() => {
    if (!activeForecast) return emptyObject;

    const tableList = makeTableList(allowForecastsWhitoutConsensus, view);

    const tableRows = (series?.forecasts?.rows || []).map((item, i) => ({
      ...item,
      header: item.date,
      reported: series.forecasts.series.kpi?.actuals?.[i],
      reported_yoy: series.forecasts.series.kpi?.change?.[i],
    }));

    const nextRows = tableList.reduce((memo, name) => ({
      ...memo,
      [name]: [
        tableNames[name],
        ...tableRows.map(row => row[name]),
      ],
    }), {});

    return {
      ...activeForecast,
      forecasts: {
        ...activeForecast.forecasts,
        rows: nextRows,
      },
    };
  }, [view, series, activeForecast]);

  const screenshotParamsRef = useRef({ positions: {} });

  screenshotParamsRef.current = {
    kpi_name: activeForecast.kpi_name,
    kpi_id: activeForecast.kpi_id,
    view,
    ticker: metadata?.ticker,
    magnitude: activeForecast.forecasts?.magnitude,
    positions: screenshotParamsRef.current.positions,
  };

  const handleScreenshot = useCallback(() => {
    const searchParams = qs.stringify(screenshotParamsRef.current, { arrayFormat: 'brackets' });
    const screenshotUrl = `/kpi_forecasts/quarterly_performance_chart.png?${searchParams}`;

    window.open(screenshotUrl, '_blank');
  }, []);

  const handleChartZoomChange = useCallback(({ min, max, columnKey }) => {
    onChartZoomChange({ columnKey });
    screenshotParamsRef.current.positions = { min, max, columnKey };
  }, []);

  return (
    <div className="kpi-forecasts-block__main-current">
      <div className="card-box__body forecasts-main">
        <div className="forecasts-main-content w-100">
          <ForecastsChartLayout
            view={view}
            title={activeForecast.forecasts?.magnitude ? `${activeForecast.kpi_name} (${activeForecast.forecasts?.magnitude})` : activeForecast.kpi_name}
            disabled={pending}
            options={[
              { text: '#', value: 'actuals' },
              { text: '%', value: 'change' },
            ]}
            showSourceText={!allowForecastsWhitoutConsensus}
            setView={setView}
            onScreenshot={handleScreenshot}
          >
            {pending || isEmpty(series?.forecasts)
              ? skeletons.forecastsMainChart
              : (
                <QuarterlyPerformanceChart
                  series={series?.forecasts?.series}
                  view={view}
                  onChartZoomChange={handleChartZoomChange}
                />
              )}
          </ForecastsChartLayout>
          <div className="forecasts-main-content__table">
            {pending
              ? skeletons.forecastsMainTable
              : (
                <ForecastsTableScrollableContainer
                  activeForecast={tableForecast}
                  scrollableContainerRef={scrollableContainerRef}
                />
              )}
          </div>
          <div className="forecasts-main-content__footer">
            <div className="highcharts-watermark" />
            {!allowForecastsWhitoutConsensus && (
              <ConsensusSourceText className="forecasts-main-content__footer-description" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

QuarterlyPerformance.propTypes = {
  series: T.object,
  activeForecast: T.object,
  pending: T.bool,
  metadata: T.object,
  allowForecastsWhitoutConsensus: T.bool,
};

export default QuarterlyPerformance;
